import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

export const generateComparisonPDF = (comparisonData, results, purpose) => {
  try {
    const doc = new jsPDF();
    let yPos = 20;
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(59, 130, 246);
    doc.text('EmoQ.ai - Image Comparison Analysis', 20, yPos);
    yPos += 15;
    
    // Purpose
    if (purpose) {
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Purpose:', 20, yPos);
      yPos += 7;
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(String(purpose), 170);
      doc.text(lines, 20, yPos);
      yPos += (lines.length * 6) + 10;
    }
    
    // Winner Section
    doc.setFillColor(255, 243, 205);
    doc.rect(15, yPos - 5, 180, 25, 'F');
    doc.setFontSize(14);
    doc.setTextColor(139, 69, 19);
    doc.text('Winner: Image ' + (results[0].imageIndex + 1), 20, yPos + 5);
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    yPos += 12;
    doc.text('Score: ' + results[0].overallScore.toFixed(1), 20, yPos);
    doc.text('Valence: ' + ((results[0].valence || 0) * 100).toFixed(0) + '%', 80, yPos);
    yPos += 15;
    
    // Overview Table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('All Images Ranked', 20, yPos);
    yPos += 7;
    
    const tableData = results.map((r, i) => [
      (i + 1).toString(),
      'Image ' + (r.imageIndex + 1),
      r.overallScore.toFixed(1),
      r.dominant_emotion,
      ((r.valence || 0) * 100).toFixed(0) + '%'
    ]);
    
    doc.autoTable({
      startY: yPos,
      head: [['Rank', 'Image', 'Score', 'Emotion', 'Valence']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] }
    });
    
    // Demographics (new page)
    doc.addPage();
    yPos = 20;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Demographic Analysis', 20, yPos);
    yPos += 10;
    
    results.forEach((result, idx) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(11);
      doc.setTextColor(59, 130, 246);
      doc.text('Image ' + (result.imageIndex + 1), 20, yPos);
      yPos += 6;
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      
      Object.entries(result.audience_reactions || {}).forEach(([group, reaction]) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFont('helvetica', 'bold');
        doc.text(group + ':', 25, yPos);
        yPos += 4;
        doc.setFont('helvetica', 'normal');
        const reactionLines = doc.splitTextToSize(String(reaction), 160);
        doc.text(reactionLines, 30, yPos);
        yPos += (reactionLines.length * 4) + 3;
      });
      yPos += 5;
    });
    
    // Footer
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text('Generated by EmoQ.ai', 20, 285);
    }
    
    doc.save('emoQ-comparison-' + Date.now() + '.pdf');
    return true;
  } catch (error) {
    console.error('PDF Error:', error);
    throw error;
  }
};

export const generateSingleAnalysisPDF = (analysis) => {
  try {
    const doc = new jsPDF();
    let yPos = 20;
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(59, 130, 246);
    doc.text('EmoQ.ai - Analysis Report', 20, yPos);
    yPos += 15;
    
    // Custom Question
    if (analysis.custom_question) {
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Your Question:', 20, yPos);
      yPos += 7;
      doc.setFont('helvetica', 'normal');
      const qLines = doc.splitTextToSize(String(analysis.custom_question), 170);
      doc.text(qLines, 20, yPos);
      yPos += (qLines.length * 6) + 10;
    }
    
    // Key Metrics
    doc.setFillColor(255, 243, 205);
    doc.rect(15, yPos - 5, 180, 20, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Emotion: ' + analysis.dominant_emotion, 20, yPos + 5);
    doc.setFontSize(10);
    yPos += 10;
    doc.text('Valence: ' + Math.round(analysis.valence * 100) + '%', 20, yPos);
    doc.text('Arousal: ' + Math.round(analysis.arousal_level * 100) + '%', 80, yPos);
    yPos += 15;
    
    // Custom Answer
    if (analysis.custom_answer) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Analysis:', 20, yPos);
      yPos += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const aLines = doc.splitTextToSize(String(analysis.custom_answer), 170);
      doc.text(aLines, 20, yPos);
      yPos += (aLines.length * 5) + 10;
    }
    
    // Emotions Table
    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Emotion Breakdown', 20, yPos);
    yPos += 7;
    
    const emotionData = Object.entries(analysis.emotions || {})
      .sort(([,a], [,b]) => b - a)
      .map(([emotion, value]) => [
        emotion.charAt(0).toUpperCase() + emotion.slice(1),
        Math.round(value) + '%'
      ]);
    
    doc.autoTable({
      startY: yPos,
      head: [['Emotion', 'Score']],
      body: emotionData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] }
    });
    
    // Demographics (new page)
    doc.addPage();
    yPos = 20;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Audience Reactions', 20, yPos);
    yPos += 10;
    
    Object.entries(analysis.audience_reactions || {}).forEach(([group, reaction]) => {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFontSize(10);
      doc.setTextColor(59, 130, 246);
      doc.text(group, 20, yPos);
      yPos += 5;
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const rLines = doc.splitTextToSize(String(reaction), 170);
      doc.text(rLines, 20, yPos);
      yPos += (rLines.length * 4) + 6;
    });
    
    // Recommendations
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Recommendations', 20, yPos);
    yPos += 10;
    
    (analysis.recommendations || []).forEach((rec, idx) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const recLines = doc.splitTextToSize((idx + 1) + '. ' + String(rec), 170);
      doc.text(recLines, 20, yPos);
      yPos += (recLines.length * 4) + 3;
    });
    
    // Footer
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text('Generated by EmoQ.ai', 20, 285);
    }
    
    doc.save('emoQ-analysis-' + Date.now() + '.pdf');
    return true;
  } catch (error) {
    console.error('PDF Error:', error);
    throw error;
  }
};

/**
 * Generate PDF from HTML content - captures visual layout with colors
 * This uses html2canvas to capture the rendered page
 */
export const generatePDFFromHTML = async (elementId = 'analysis-results-pdf', fileName = 'emoQ-analysis') => {
  try {
    console.log('Starting HTML-to-PDF generation...');
    
    // Find the element to convert
    const element = document.getElementById(elementId);
    
    if (!element) {
      throw new Error(`Element with id '${elementId}' not found`);
    }

    // Show loading indicator in console
    console.log('Capturing page content...');
    
    // Ensure element is visible and rendered
    if (element.offsetWidth === 0 || element.offsetHeight === 0) {
      throw new Error('Element is not visible or has no dimensions');
    }
    
    // Wait for all images to load before capturing
    const images = element.getElementsByTagName('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve; // Resolve even on error to prevent hanging
        setTimeout(resolve, 15000); // Timeout after 15 seconds
      });
    });
    await Promise.all(imagePromises);

    // Configure html2canvas with optimized settings for better alignment
    const canvas = await html2canvas(element, {
      scale: 1.2, // Reduced scale for better performance
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false, // Disable logging for cleaner output
      imageTimeout: 0, // We already waited for images
      width: 1200, // Fixed width for consistent layout
      windowWidth: 1200,
      height: Math.min(element.scrollHeight, 15000), // Limit max height to prevent memory issues
      onclone: (clonedDoc) => {
        const clonedElement = clonedDoc.getElementById(elementId);
        if (clonedElement) {
          clonedElement.style.display = 'block';
          clonedElement.style.position = 'relative';
          clonedElement.style.visibility = 'visible';
          clonedElement.style.width = '1200px';
          clonedElement.style.maxWidth = '1200px';
          
          // Show all hidden tabs for PDF generation
          const hiddenElements = clonedElement.querySelectorAll('.hidden');
          hiddenElements.forEach(el => {
            el.classList.remove('hidden');
            el.style.display = 'block';
          });
          
          // Fix alignment issues and handle gradients
          const allElements = clonedElement.querySelectorAll('*');
          allElements.forEach(el => {
            el.style.boxSizing = 'border-box';
            el.style.textAlign = el.style.textAlign || 'left';
            
            // Replace gradient backgrounds with solid colors for html2canvas compatibility
            const computedStyle = window.getComputedStyle(el);
            const bgImage = computedStyle.backgroundImage;
            
            if (bgImage && bgImage.includes('gradient')) {
              // Map gradient classes to solid fallback colors
              const elementClasses = String(el.className || '');
              
              // Purple/pink gradient backgrounds (AIEnhancement card)
              if (elementClasses.includes('from-purple-100') || elementClasses.includes('from-purple-900')) {
                el.style.backgroundImage = 'none';
                el.style.backgroundColor = elementClasses.includes('from-purple-900') ? '#a855f7' : '#f3e8ff';
              }
              // Blue gradient buttons or headers
              else if (elementClasses.includes('from-blue')) {
                el.style.backgroundImage = 'none';
                el.style.backgroundColor = '#3b82f6';
              }
              // Green gradients
              else if (elementClasses.includes('from-green')) {
                el.style.backgroundImage = 'none';
                el.style.backgroundColor = '#10b981';
              }
              // Other gradients - use light purple as fallback
              else if (bgImage.includes('gradient')) {
                el.style.backgroundImage = 'none';
                el.style.backgroundColor = '#f3e8ff';
              }
            }
            
            // Force white backgrounds for cards
            const elementClasses = String(el.className || '');
            if (elementClasses.includes('bg-white') || elementClasses.includes('Card')) {
              el.style.backgroundColor = '#ffffff';
            }
          });
        }
      }
    });
    
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Failed to create canvas from element');
    }

    console.log('Canvas created, generating PDF...');

    // Calculate PDF dimensions with margins
    const pdfWidth = 210; // A4 width in mm
    const pdfHeight = 297; // A4 height in mm
    const margin = 10; // 10mm margins
    const contentWidth = pdfWidth - (2 * margin);
    const contentHeight = pdfHeight - (2 * margin);
    
    // Calculate image dimensions maintaining aspect ratio
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * contentWidth) / canvas.width;
    
    // Create PDF document
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    // Convert canvas to image
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    
    // Calculate how many pages we need
    let heightLeft = imgHeight;
    let position = 0;
    let pageNumber = 1;
    
    // Add first page with margin
    pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight);
    heightLeft -= contentHeight;
    
    // Add additional pages if needed with improved logic
    const maxPages = 30; // Limit to 30 pages to prevent crashes
    let pageCount = 0;
    
    while (heightLeft > 0 && pageCount < maxPages) {
      position = -(contentHeight * pageNumber);
      pdf.addPage();
      
      try {
        pdf.addImage(imgData, 'JPEG', margin, position + margin, imgWidth, imgHeight);
      } catch (error) {
        console.error('Error adding image to PDF page:', error);
        break;
      }
      
      heightLeft -= contentHeight;
      pageNumber++;
      pageCount++;
    }
    
    // Remove last empty page if exists
    if (pageCount > 0 && pdf.internal.pages.length > pageCount + 1) {
      pdf.deletePage(pdf.internal.pages.length);
    }

    // Add metadata
    pdf.setProperties({
      title: `EmoQ.ai Analysis Report - ${fileName}`,
      subject: 'Emotional Intelligence Analysis',
      author: 'EmoQ.ai',
      keywords: 'emotional analysis, AI, sentiment',
      creator: 'EmoQ.ai'
    });

    // Save the PDF
    const timestamp = Date.now();
    pdf.save(`${fileName}-${timestamp}.pdf`);
    
    console.log('PDF generated and downloaded successfully!');
    return true;
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
};
